import React from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import App from './App';
import RouteErrorPage from './components/RouteErrorPage';
import AppLayout from './components/AppLayout';
import ReportStudioLayout from './components/ReportStudioLayout';
import LoginPage from './pages/login/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';

import { queryClient } from './components/queryClient';
import { getAccountById } from './services/accounts/accountsService';
import { getCommodityById } from './services/commodities/commoditiesService';
import { getHaulierById } from './services/hauliers/hauliersService';
import { getLaAreaById } from './services/laAreas/laAreaService';

/* Tables – lazy-loaded for code splitting */
const Accounts = React.lazy(() => import('./pages/accounts/Accounts'));
const Commodities = React.lazy(() => import('./pages/commodities/Commodities'));
const Hauliers = React.lazy(() => import('./pages/hauliers/Hauliers'));
const LaAreas = React.lazy(() => import('./pages/laAreas/LaAreas'));
const CompletedLoads = React.lazy(() => import('./pages/loads/completedLoads/CompletedLoads'));
const Users = React.lazy(() => import('./pages/users/Users'));
const RolesPage = React.lazy(() => import('./pages/roles/RolesPage'));
const PermissionsPage = React.lazy(() => import('./pages/permissions/PermissionsPage'));
const TablePermissionsPage = React.lazy(() => import('./pages/permissions/TablePermissionsPage'));
const Drivers = React.lazy(() => import('./pages/drivers/Drivers'));
const Locations = React.lazy(() => import('./pages/locations/locations'));
const Depots = React.lazy(() => import('./pages/depots/Depots'));
const Ships = React.lazy(() => import('./pages/ships/Ships'));
const Vehicles = React.lazy(() => import('./pages/vehicles/Vehicles'));
const Trailers = React.lazy(() => import('./pages/trailers/Trailers'));
const Contracts = React.lazy(() => import('./pages/contracts/Contracts'));
const Reports = React.lazy(() => import('./pages/reports/Reports'));
const IncompleteLoads = React.lazy(() => import('./pages/loads/incompleteLoads/IncompleteLoads'));

const SettingsPage = React.lazy(() => import('./pages/settings/SettingsPage'));
const SettingsGeneral = React.lazy(() => import('./pages/settings/SettingsGeneral'));
const SettingsCustomFields = React.lazy(() => import('./pages/settings/SettingsCustomFields'));
const SettingsWeighing = React.lazy(() => import('./pages/settings/SettingsWeighing'));
const SettingsLookups = React.lazy(() => import('./pages/settings/SettingsLookups'));
const SettingsWeighingConfig = React.lazy(() => import('./pages/settings/SettingsWeighingConfig'));
const SettingsWeighbridges = React.lazy(() => import('./pages/settings/SettingsWeighbridges'));
const SettingsSecurity = React.lazy(() => import('./pages/settings/SettingsSecurity'));
const SettingsAccount = React.lazy(() => import('./pages/settings/SettingsAccount'));
const SettingsCommodity = React.lazy(() => import('./pages/settings/SettingsCommodity'));
const SettingsHaulier = React.lazy(() => import('./pages/settings/SettingsHaulier'));
const SettingsLaArea = React.lazy(() => import('./pages/settings/SettingsLaArea'));
const SettingsDriver = React.lazy(() => import('./pages/settings/SettingsDriver'));
const SettingsFromLocation = React.lazy(() => import('./pages/settings/SettingsFromLocation'));
const SettingsToLocation = React.lazy(() => import('./pages/settings/SettingsToLocation'));
const SettingsContracts = React.lazy(() => import('./pages/settings/SettingsContracts'));
const SettingsShip = React.lazy(() => import('./pages/settings/SettingsShip'));
const SettingsLoads = React.lazy(() => import('./pages/settings/SettingsLoads'));
const SettingsTerminology = React.lazy(() => import('./pages/settings/SettingsTerminology'));
const ReportDesignerPage = React.lazy(() => import('./pages/reports/ReportDesignerPage'));
const ReportViewerPage = React.lazy(() => import('./pages/reports/ReportViewerPage'));

/* Forms – lazy-loaded */
const AccountFormPage = React.lazy(() => import('./pages/accounts/AccountsFormPage'));
const CommoditiesFormPage = React.lazy(() => import('./pages/commodities/CommoditiesFormPage'));
const HauliersFormPage = React.lazy(() => import('./pages/hauliers/HauliersFormPage'));
const LaAreasFormPage = React.lazy(() => import('./pages/laAreas/LaAreasFormPage'));
const UsersFormPage = React.lazy(() => import('./pages/users/UsersFormPage'));
const DriversFormPage = React.lazy(() => import('./pages/drivers/DriversFormPage'));
const LocationsFormPage = React.lazy(() => import('./pages/locations/locationsFormPage'));
const DepotsFormPage = React.lazy(() => import('./pages/depots/DepotsFormPage'));
const ShipsFormPage = React.lazy(() => import('./pages/ships/ShipsFormPage'));
const VehiclesFormPage = React.lazy(() => import('./pages/vehicles/VehiclesFormPage'));
const TrailersFormPage = React.lazy(() => import('./pages/trailers/TrailersFormPage'));
const ContractsFormPage = React.lazy(() => import('./pages/contracts/ContractsFormPage'));
const CompletedLoadFormPage = React.lazy(() => import('./pages/loads/completedLoads/CompletedLoadFormPage'));
const DeletedLoads = React.lazy(() => import('./pages/loads/deletedLoads/DeletedLoads'));
const DeletedLoadFormPage = React.lazy(() => import('./pages/loads/deletedLoads/DeletedLoadFormPage'));

/* -------------------- Loaders -------------------- */
// List pages use infinite queries with a different key (search/sort/filter/cursor).
// Prefetching GET getEnabled here never hydrates the grid and extra-fetches on every visit.

async function accountDetailLoader({ params }) {
    const id = params.id;
    if (!id || id === 'new') return null;
    await queryClient.ensureQueryData({
        queryKey: ['account', id],
        queryFn: ({ signal }) => getAccountById(id, signal),
    });
    return null;
}

async function commodityDetailLoader({ params }) {
    const id = params.id;
    if (!id || id === 'new') return null;
    await queryClient.ensureQueryData({
        queryKey: ['commodity', id],
        queryFn: ({ signal }) => getCommodityById(id, signal),
    });
    return null;
}

async function haulierDetailLoader({ params }) {
    const id = params.id;
    if (!id || id === 'new') return null;
    await queryClient.ensureQueryData({
        queryKey: ['haulier', id],
        queryFn: ({ signal }) => getHaulierById(id, signal),
    });
    return null;
}

async function laAreaDetailLoader({ params }) {
    const id = params.id;
    if (!id || id === 'new') return null;
    await queryClient.ensureQueryData({
        queryKey: ['laArea', id],
        queryFn: ({ signal }) => getLaAreaById(id, signal),
    });
    return null;
}

/* -------------------- Router -------------------- */
export const router = createBrowserRouter([
    // Public
    { path: '/login', element: <LoginPage /> },

    // Protected app routes
    {
        element: <App />,
        errorElement: <RouteErrorPage />,
        children: [
            {
                element: <AppLayout />,
                children: [
            { index: true, element: <DashboardPage /> },

            // Accounts
            { path: 'accounts', element: <Accounts /> },
            { path: 'accounts/new', element: <AccountFormPage /> },
            { path: 'accounts/:id/edit', element: <AccountFormPage />, loader: accountDetailLoader },

            // Commodities
            { path: 'commodities', element: <Commodities /> },
            { path: 'commodities/new', element: <CommoditiesFormPage /> },
            { path: 'commodities/:id/edit', element: <CommoditiesFormPage />, loader: commodityDetailLoader },

            // Hauliers
            { path: 'hauliers', element: <Hauliers /> },
            { path: 'hauliers/new', element: <HauliersFormPage /> },
            { path: 'hauliers/:id/edit', element: <HauliersFormPage />, loader: haulierDetailLoader },

            // LA Areas
            { path: 'la-areas', element: <LaAreas /> },
            { path: 'la-areas/new', element: <LaAreasFormPage /> },
            { path: 'la-areas/:id/edit', element: <LaAreasFormPage />, loader: laAreaDetailLoader },

            // Drivers
            { path: 'drivers', element: <Drivers /> },
            { path: 'drivers/new', element: <DriversFormPage /> },
            { path: 'drivers/:id/edit', element: <DriversFormPage /> },

            // Locations (STORE)
            { path: 'locations', element: <Locations /> },
            { path: 'locations/new', element: <LocationsFormPage /> },
            { path: 'locations/:id/edit', element: <LocationsFormPage /> },

            // Depots (DEPOT)
            { path: 'depots', element: <Depots /> },
            { path: 'depots/new', element: <DepotsFormPage /> },
            { path: 'depots/:id/edit', element: <DepotsFormPage /> },

            // Ships
            { path: 'ships', element: <Ships /> },
            { path: 'ships/new', element: <ShipsFormPage /> },
            { path: 'ships/:id/edit', element: <ShipsFormPage /> },

            // Vehicles
            { path: 'vehicles', element: <Vehicles /> },
            { path: 'vehicles/new', element: <VehiclesFormPage /> },
            { path: 'vehicles/:id/edit', element: <VehiclesFormPage /> },

            // Trailers
            { path: 'trailers', element: <Trailers /> },
            { path: 'trailers/new', element: <TrailersFormPage /> },
            { path: 'trailers/:id/edit', element: <TrailersFormPage /> },

            // Contracts
            { path: 'contracts', element: <Contracts /> },
            { path: 'contracts/new', element: <ContractsFormPage /> },
            { path: 'contracts/:id/edit', element: <ContractsFormPage /> },

            // Loads: Weighing (incomplete) and Completed (infinite list)
            { path: 'loads/weighing', element: <IncompleteLoads /> },
            { path: 'loads/completed', element: <CompletedLoads /> },
            { path: 'loads/completed/:id', element: <CompletedLoadFormPage /> },
            { path: 'loads/deleted', element: <DeletedLoads /> },
            { path: 'loads/deleted/:id', element: <DeletedLoadFormPage /> },

            // Users
            {
                path: 'users',
                children: [
                    { index: true, element: <Users /> },
                    { path: 'roles', element: <RolesPage /> },
                    { path: 'new', element: <UsersFormPage /> },
                    { path: ':id/edit', element: <UsersFormPage /> },
                ],
            },

            { path: 'permissions/form', element: <PermissionsPage /> },
            { path: 'permissions/table', element: <TablePermissionsPage /> },

            // Settings (General, Weighing, Security)
            {
                path: 'settings',
                element: <SettingsPage />,
                children: [
                    { index: true, element: <Navigate to="/settings/general" replace /> },
                    { path: 'general', element: <SettingsGeneral /> },
                    { path: 'terminology', element: <SettingsTerminology /> },
                    { path: 'customFields', element: <SettingsCustomFields /> },
                    { path: 'weighing', element: <SettingsWeighing /> },
                    { path: 'lookups', element: <SettingsLookups /> },
                    { path: 'weighingConfig', element: <SettingsWeighingConfig /> },
                    { path: 'weighbridges', element: <SettingsWeighbridges /> },
                    { path: 'security', element: <SettingsSecurity /> },
                    { path: 'account', element: <SettingsAccount /> },
                    { path: 'commodity', element: <SettingsCommodity /> },
                    { path: 'haulier', element: <SettingsHaulier /> },
                    { path: 'laArea', element: <SettingsLaArea /> },
                    { path: 'driver', element: <SettingsDriver /> },
                    { path: 'fromLocation', element: <SettingsFromLocation /> },
                    { path: 'toLocation', element: <SettingsToLocation /> },
                    { path: 'contracts', element: <SettingsContracts /> },
                    { path: 'ship', element: <SettingsShip /> },
                    { path: 'loads', element: <SettingsLoads /> },
                ],
            },

            // Reports list + viewer (designer uses ReportStudioLayout — outside AppLayout)
            { path: 'reports', element: <Reports /> },
            { path: 'reports/view/:url', element: <ReportViewerPage /> },
                ],
            },
            {
                element: <ReportStudioLayout />,
                children: [
                    { path: 'reports/designer/:reportId', element: <ReportDesignerPage /> },
                ],
            },
        ],
    },
    { path: '*', element: <Navigate to="/login" replace /> },
]);